import { useState } from 'react';
import { T, useGT } from 'gt-react';
import {
  parseTaskActionFormSchema,
  type TaskActionFormField,
  type TaskTypeAction,
} from '@erp/shared-types';
import type { TaskResponse, TaskTypeResponse } from '@erp/rpc';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components/ui/dialog';
import { toast } from 'src/components/ui/toast';
import { DynamicSchemaForm } from 'src/components/common/DynamicSchemaForm';
import { useExecuteTaskAction } from 'src/api/rpc/hooks/tasks/useTaskDetail';
import {
  buildTaskActionPayload,
  initialTaskActionValues,
  missingRequiredActionFields,
  taskActionFieldsToSchemaFields,
} from './taskActionForm';

interface PendingAction {
  action: TaskTypeAction;
  fields: TaskActionFormField[];
}

/**
 * Custom action buttons declared on the task type. Formless actions run on
 * click; formful ones open a dialog. Intended for the task review header
 * toolbar alongside assignee + close controls.
 */
export function TaskActionsPanel({
  task,
  taskType,
}: {
  task: TaskResponse;
  taskType: TaskTypeResponse;
}) {
  const gt = useGT();
  const executeAction = useExecuteTaskAction(task.id);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});

  const actions = taskType.actions ?? [];
  if (actions.length === 0) return null;

  async function run(actionKey: string, payload?: Record<string, unknown>) {
    try {
      const result = await executeAction.mutateAsync({
        id: task.id,
        actionKey,
        payload,
      });
      setPending(null);
      if (result.effect.kind === 'start_workflow') {
        toast.success(
          gt('Action recorded — workflow started', {
            context: 'toast after a task action started a workflow',
          }),
        );
      } else if (result.effect.kind === 'set_outcome') {
        toast.success(
          gt('Action recorded — outcome saved', {
            context: 'toast after a task action recorded an outcome',
          }),
        );
      } else {
        toast.success(
          gt('Action recorded', {
            context: 'toast after a task action was executed',
          }),
        );
      }
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : gt('Action failed', {
              context: 'toast after a task action failed to execute',
            }),
      );
    }
  }

  function handleActionClick(action: TaskTypeAction) {
    const parsed = parseTaskActionFormSchema(action.formSchema);
    if (!parsed.ok) {
      toast.error(
        gt(
          'This action has an invalid form schema; fix the task type configuration.',
          {
            context: 'toast when a declared task action form schema is invalid',
          },
        ),
      );
      return;
    }
    if (!parsed.fields || parsed.fields.length === 0) {
      void run(action.key);
      return;
    }
    setFormValues(initialTaskActionValues(parsed.fields));
    setPending({ action, fields: parsed.fields });
  }

  const missingRequired = pending
    ? missingRequiredActionFields(pending.fields, formValues)
    : [];

  return (
    <div
      className="flex flex-wrap items-center gap-1.5"
      data-testid="task-actions-panel"
    >
      {actions.map((action) => (
        <Button
          key={action.key}
          variant="outline"
          size="sm"
          disabled={executeAction.isPending}
          onClick={() => handleActionClick(action)}
        >
          {action.label}
        </Button>
      ))}

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{pending?.action.label}</DialogTitle>
          </DialogHeader>
          {pending && (
            <DynamicSchemaForm
              fields={taskActionFieldsToSchemaFields(pending.fields)}
              values={formValues}
              onChange={setFormValues}
            />
          )}
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setPending(null)}
              disabled={executeAction.isPending}
            >
              <T context="button, close the task action dialog">Cancel</T>
            </Button>
            <Button
              disabled={executeAction.isPending || missingRequired.length > 0}
              onClick={() => {
                if (!pending) return;
                void run(
                  pending.action.key,
                  buildTaskActionPayload(pending.fields, formValues),
                );
              }}
            >
              {executeAction.isPending ? (
                <T context="button label while a task action is executing">
                  Running…
                </T>
              ) : (
                <T context="button, submit the task action form">Run action</T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
